import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, Mail, Lock, Eye, EyeOff, ArrowLeft, Briefcase, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Demo users for quick login
const DEMO_USERS = [
  {
    email: 'desarrollador@broco.mx',
    password: 'demo123',
    name: 'Carlos Mendoza',
    role: 'Desarrollador',
    icon: Building2,
    color: 'bg-[#C05621]'
  },
  {
    email: 'constructor@broco.mx',
    password: 'demo123',
    name: 'Roberto García',
    role: 'Constructor',
    icon: Briefcase,
    color: 'bg-[#81A183]'
  }
];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const fillDemoUser = (user) => {
    setFormData({
      email: user.email,
      password: user.password
    });
    toast.success(`Credenciales de ${user.role} cargadas`);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(formData.email, formData.password);
      
      // Verificar que el token se guardó antes de navegar
      const token = localStorage.getItem('access_token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('No se pudo guardar el token de autenticación');
      }
      
      toast.success('Bienvenido a BROCO');
      // Pequeño delay para asegurar que el token se guardó
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (error) {
      console.error('Error en login:', error);
      const message = error.response?.data?.message || error.response?.data?.detail || error.message || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C554B] hover:text-[#C05621] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#C05621] rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <span className="font-bold text-2xl text-[#2D2A26]">BROCO</span>
          </div>

          <h1 className="text-3xl font-bold text-[#2D2A26] mb-2">Bienvenido de vuelta</h1>
          <p className="text-[#5C554B] mb-6">Ingresa tus credenciales para acceder</p>

          {/* Demo Users Quick Login */}
          <div className="mb-6 p-4 bg-[#EBE5DA]/50 rounded-xl border border-[#E2E8F0]">
            <p className="text-sm text-[#5C554B] mb-3 font-medium">Acceso rápido (Demo)</p>
            <div className="grid grid-cols-2 gap-3">
              {DEMO_USERS.map((user) => (
                <button
                  key={user.email}
                  type="button"
                  onClick={() => fillDemoUser(user)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white hover:border-[#C05621] hover:shadow-sm transition-all text-left group"
                  data-testid={`demo-${user.role.toLowerCase()}`}
                >
                  <div className={`w-10 h-10 ${user.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <user.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-[#2D2A26] text-sm truncate">{user.name}</p>
                    <p className="text-xs text-[#5C554B]">{user.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                Correo electrónico
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className="input-default pl-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#2D2A26] font-medium">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="input-default pl-12 pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#5C554B] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full h-12 text-base"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#5C554B]">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-[#C05621] font-semibold hover:underline">
              Regístrate aquí
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1716698288651-b0b8698ea2f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxibHVlcHJpbnQlMjBvbiUyMHRhYmxlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzAyNDM4MjF8MA&ixlib=rb-4.1.0&q=85"
          alt="Blueprint"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2D2A26]/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white">
            <p className="text-2xl font-medium mb-4">
              "BROCO nos permite tener visibilidad completa de nuestras obras, desde el presupuesto hasta la ejecución."
            </p>
            <footer className="text-white/80">
              — Director de Proyectos
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Login;
